import { OneIdScriptLoaderService } from '../../services/one-id-script-loader/one-id-script-loader.service';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Injectable } from '@angular/core';

import { RaInjectOneidAuthTokenService } from '@wdpr/ra-angular-oneid-auth-token-injector';

import { ConfigService } from '../../services/config/config.service';
import { NativeBridgeService } from '../../native-bridge/native-bridge.service';
import { OneIdConfig } from '../../interfaces/one-id-config.interface';
import { OneIdHybridGuest } from '../../services/interfaces/one-id-hybrid-guest';
import { OneIdService } from '../../services/one-id/one-id.service';
import { TravelAgentService } from '../../travel-agent/travel-agent.service';
import { WindowRef } from '../../window-ref/window-ref.service';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { OneIdInitializationGuardConfig } from '../../interfaces/one-id-initialization-guard-config';
import { RAOneIdInjectorServiceConfig } from '../../interfaces/ra-one-id-injector-service-config';

@Injectable()
export class CanActivateIsInitializedOneIdGuard implements CanActivate {
    oneIdConfig: OneIdConfig;

    constructor(
        private configService: ConfigService,
        private loggerService: LoggerService,
        private nativeBridgeService: NativeBridgeService,
        private oneIdScriptLoaderService: OneIdScriptLoaderService,
        private oneIdService: OneIdService,
        private raInjectOneidAuthTokenService: RaInjectOneidAuthTokenService,
        private travelAgentService: TravelAgentService,
        private windowRef: WindowRef
    ) {
        this.oneIdConfig = this.configService.getValue('oneId');
    }

    async canActivate(next: ActivatedRouteSnapshot): Promise<boolean> {
        return await this.initOneId(next);
    }

    /**
     * This function seeks to load Authenticator JS, initialize PEP object and RA lib
     * taking into account these excenarios:
     *
     * Web flow:
     *  - Guest flow: load Authenticator JS, initialize PEP object and RA lib
     *  - Travel Agent flow: not load authentication not initialize the PEP object nor RA lib
     * Hybrid flow:
     *  - Do not load Authenticator JS and initialize RA lib.
     * @returns Promise<boolean>
     */
    private async initOneId(next: ActivatedRouteSnapshot): Promise<boolean> {
        const guardConfig = next.data?.oneIdInitializationConfig as OneIdInitializationGuardConfig;
        let scriptloadedAndInitialized: boolean;

        // Do not initialize RA Lib injector if travel agent is logged in
        if (this.travelAgentService.isTravelAgentLoggedIn()) {
            return true;
        }

        // Only initialize RA Lib injector if hybrid
        if (this.nativeBridgeService.isEmbedded(true)) {
            this.loggerService.log('OneId guard: is Embedded');
            let hybridGuest;

            await this.initRaInjectOneidAuthTokenService();

            this.loggerService.log('OneId guard: Ra inject library initilized');
            hybridGuest = await this.raInjectOneidAuthTokenService.getOneIdData();
            this.loggerService.log('OneId guard: hybrid guest', hybridGuest);
            this.oneIdService.setHybridGuest(hybridGuest as OneIdHybridGuest);
            this.loggerService.log('OneId guard: hybrid guest service', this.oneIdService.hybridGuest);
            this.loggerService.log('OneId guard: swid service', this.oneIdService.swid);

            return true;
        }

        // Initialize PEP object and RA Lib
        if (!this.windowRef?.PEP?.PROFILE_AUTH?.isInitialized) {
            scriptloadedAndInitialized = await this.oneIdScriptLoaderService.loadJsScript(this.oneIdConfig);
        } else {
            scriptloadedAndInitialized = true;
        }

        if (scriptloadedAndInitialized && !guardConfig?.avoidInitializeRAInjectorService) {
            this.loggerService.log('OneId guard: Ra inject library initilized');
            await this.initRaInjectOneidAuthTokenService(guardConfig?.RaOneIdInjectorServiceConfig);
        }

        return true;
    }

    /**
     * Initialize RA Lib which injetcs one id token to authorization header
     */
    private async initRaInjectOneidAuthTokenService(raServiceConfig?: RAOneIdInjectorServiceConfig) {
        await this.raInjectOneidAuthTokenService.init({
            ENABLED: raServiceConfig?.enabled ?? true,
            NONLOGGEDINFLOW: raServiceConfig?.nonLoggedInFlow ?? true,
            LOGGEDINFLOW: raServiceConfig?.loggedInFlow ?? true,
        });
    }
}

